/** The route-facing API over ONE compose library (its module catalog and
 * template catalog). Each entry point is a module-level function of the
 * library (`*In`), so bundlers keep only what a caller uses; `composeApi`
 * binds them all to one library, and the plain named exports are the
 * maintained library's binding. Everything here is deterministic and
 * provider-free. */
import { validateGameSpec, type SpecValidationV1 } from './spec.js';
import { compileGameSpec, type CompileOptionsV1, type CompiledGameV1 } from './compile.js';
import { proveGameSpec, proveGameSpecAsync, ProofScheduleV1, runProofTask, runProofTaskAsync, type ProofOptionsV1, type ProofResultV1, type ProofRunV1,
  type ProofTaskV1 } from './proof/prove.js';
import { deriveGameSpecPresentation, type ComposedPresentationV1 } from './presentation.js';
import { instantiateTemplate, specModuleIds, validateGeneratedTemplate, validateGeneratedTemplateDraft, validateGeneratedTemplateStructure, validateGeneratedTemplateDraftStructure, validateTemplatePatch, type TemplateOverridesV1, type TemplatePatchValidationV1,
  type TemplateV1 } from './templates/index.js';
import { MAINTAINED_LIBRARY, type ComposeLibraryV1 } from './library.js';
import type { GameSpecV1 } from './spec.js';
import type { TemplateStructureV1 } from './templates/structure.js';
import type { VerbProofV1 } from './templates/template.js';

export interface TemplateSummaryV1 {
  id: string;
  family: string;
  title: string;
  pitch: string;
  disclosure: TemplateV1['disclosure'];
  kinds: string[];
  modules: string[];
  tunables: TemplateV1['tunables'];
  contentSlots: TemplateV1['contentSlots'];
}

/** Catalog row for retrieval/LLM context. */
export interface ModuleSummaryV1 { id: string; version: number; role: string; attach: string; summary: string; events: readonly string[] }

export interface TemplateProofOptionsV1 {
  stubCheck?: boolean;
  /** Also prove an idle seat 0 loses within the builder's mission bound. */
  idleDefeat?: boolean;
  seeds?: number[];
  /** The patch's structure: the modules an adaptation added that its game
   * depends on (stub-checked with the template's own) and the events its
   * game must show (required with the template's own). */
  structure?: Pick<TemplateStructureV1, 'signature' | 'events'>;
}

export function validateSpecIn(library: ComposeLibraryV1, spec: unknown): SpecValidationV1 {
  return validateGameSpec(spec, library.modules);
}

export function compileSpecIn(library: ComposeLibraryV1, spec: unknown, options: CompileOptionsV1 = {}): CompiledGameV1 {
  return compileGameSpec(spec, library.modules, options);
}

export function derivePresentationIn(library: ComposeLibraryV1, spec: unknown): ComposedPresentationV1 {
  return deriveGameSpecPresentation(spec, library.modules);
}

export function proveSpecIn(library: ComposeLibraryV1, spec: unknown, options: Partial<ProofOptionsV1> = {}): ProofResultV1 {
  return proveGameSpec(spec, library.modules, options);
}

/** {@link proveSpecIn} that yields to the event loop while it works. */
export function proveSpecAsyncIn(library: ComposeLibraryV1, spec: unknown, options: Partial<ProofOptionsV1> = {}): Promise<ProofResultV1> {
  return proveGameSpecAsync(spec, library.modules, options);
}

export function listModulesIn(library: ComposeLibraryV1): ModuleSummaryV1[] {
  return [...library.modules.values()].map(m => ({ id: m.id, version: m.version, role: m.role, attach: m.attach, summary: m.summary, events: m.events }));
}

/** The templates a new Create may choose (retired revisions resolve by id but are never listed). */
export function listTemplatesIn(library: ComposeLibraryV1): TemplateSummaryV1[] {
  return library.templates.filter(t => !t.retired).map(t => ({
    id: t.id, family: t.family, title: t.title, pitch: t.pitch, disclosure: structuredClone(t.disclosure), kinds: Object.keys(t.spec.archetypes),
    modules: [...new Set([...t.spec.globals.map(g => g.use), ...Object.values(t.spec.archetypes).flatMap(a => a.modules.map(m => m.use))])].sort(),
    tunables: structuredClone(t.tunables), contentSlots: structuredClone(t.contentSlots),
  }));
}

export function getTemplateIn(library: ComposeLibraryV1, id: string): TemplateV1 {
  const template = library.templates.find(t => t.id === id);
  if (!template) throw new RangeError(`unknown template ${id}`);
  return structuredClone(template);
}

/** Validate an UNTRUSTED generated template against `library`'s modules
 * (templates/generated.ts); short of the solver proof. */
export function validateGeneratedTemplateIn(library: ComposeLibraryV1, template: unknown) {
  return validateGeneratedTemplate(template, library.modules);
}

/** Validate an UNTRUSTED generated-template draft (a model's reply). */
export function validateGeneratedTemplateDraftIn(library: ComposeLibraryV1, draft: unknown) {
  return validateGeneratedTemplateDraft(draft, library.modules);
}

/** Base/scaffold structure only; final effective-game verbs still need execution. */
export function validateGeneratedTemplateStructureIn(library: ComposeLibraryV1, template: unknown) {
  return validateGeneratedTemplateStructure(template, library.modules);
}

/** Scaffold draft structure only, never standalone or final admission. */
export function validateGeneratedTemplateDraftStructureIn(library: ComposeLibraryV1, draft: unknown) {
  return validateGeneratedTemplateDraftStructure(draft, library.modules);
}

/** A template with data overrides (bounded tunables, text, renames, palette). */
export function instantiateIn(library: ComposeLibraryV1, id: string, overrides: TemplateOverridesV1 = {}): GameSpecV1 {
  return instantiateTemplate(getTemplateIn(library, id), overrides, library.modules);
}

/** Validate an UNTRUSTED patch against a template (every error collected);
 * on success the instantiated, validated spec. Unknown template ids are an
 * error result, never a throw, so a repairing generator can be told. */
export function patchTemplateIn(library: ComposeLibraryV1, id: string, patch: unknown): TemplatePatchValidationV1 {
  const template = library.templates.find(t => t.id === id);
  if (!template) return { ok: false, errors: [`unknown template ${id}`] };
  return validateTemplatePatch(structuredClone(template), patch, library.modules);
}

/** The exact module closure a spec runs — `{id, version}` sorted by id —
 * which an accepted authority pins (a module version bump re-admits). */
export function specModuleVersionsIn(library: ComposeLibraryV1, spec: GameSpecV1): Array<{ id: string; version: number }> {
  const ids = new Set([...spec.globals.map(g => g.use), ...Object.values(spec.archetypes).flatMap(a => a.modules.map(m => m.use))]);
  return [...ids].sort().map(id => {
    const module = library.modules.get(id);
    if (!module) throw new RangeError(`unknown module ${id}`);
    return { id, version: module.version };
  });
}

/** The template's obligations as they apply to `spec`, which may be a
 * structural adaptation: a required event stays only while some module of
 * the spec can emit it (and its kind, when qualified, still exists); a
 * signature module stays only while the spec uses it; the patch's own
 * structure signature and events join them. An unmodified template keeps
 * every one. */
export function templateObligationsIn(library: ComposeLibraryV1, id: string, spec: GameSpecV1, rename: Readonly<Record<string, string>>,
  structure?: Pick<TemplateStructureV1, 'signature' | 'events'>): { requiredEvents: string[]; signatureModules: string[] } {
  const template = getTemplateIn(library, id);
  const used = specModuleIds(spec);
  const emittable = new Set([...used].flatMap(m => library.modules.get(m)?.events ?? []));
  const requiredEvents = template.requirements.events.map(event => {
    const [kind, archetype] = event.split(':');
    return archetype !== undefined && rename[archetype] ? `${kind}:${rename[archetype]}` : event;
  }).filter(event => {
    const [kind, archetype] = event.split(':');
    return emittable.has(kind!) && (archetype === undefined || Object.hasOwn(spec.archetypes, archetype));
  });
  return { requiredEvents: [...new Set([...requiredEvents, ...structure?.events ?? []])],
    signatureModules: [...new Set([...template.signatureModules.filter(m => used.has(m)), ...structure?.signature ?? []])] };
}

/** The native verb proofs of a game derived from template `id` under a
 * patch structure: the structure's restated ones, else the template's. */
export function gameVerbProofsIn(library: ComposeLibraryV1, id: string, structure?: Pick<TemplateStructureV1, 'verbProofs'>): VerbProofV1[] {
  return structuredClone(structure?.verbProofs ?? getTemplateIn(library, id).verbProofs);
}

function templateProofOptions(library: ComposeLibraryV1, id: string, spec: GameSpecV1, rename: Readonly<Record<string, string>>,
  options: TemplateProofOptionsV1): Partial<ProofOptionsV1> {
  const obligations = templateObligationsIn(library, id, spec, rename, options.structure);
  return {
    ...(options.seeds ? { seeds: options.seeds } : {}),
    requiredEvents: obligations.requiredEvents,
    stubCheck: options.stubCheck ? obligations.signatureModules : [],
    idleDefeat: options.idleDefeat === true,
  };
}

/** Prove a spec against a template's obligations: objective completion on
 * ≥2 seeds, every required event observed, and (optionally) the stub check
 * over the template's signature modules. `rename` is the patch's kind
 * renames: a kind-qualified obligation (`destroy:bee`) follows its kind. */
export function proveAgainstTemplateIn(library: ComposeLibraryV1, id: string, spec: GameSpecV1, rename: Readonly<Record<string, string>>,
  options: TemplateProofOptionsV1 = {}): ProofResultV1 {
  return proveSpecIn(library, spec, templateProofOptions(library, id, spec, rename, options));
}

/** {@link proveAgainstTemplateIn} that yields to the event loop while it
 * works (a test or server that must keep its RPC/heartbeats alive). */
export function proveAgainstTemplateAsyncIn(library: ComposeLibraryV1, id: string, spec: GameSpecV1, rename: Readonly<Record<string, string>>,
  options: TemplateProofOptionsV1 = {}): Promise<ProofResultV1> {
  return proveSpecAsyncIn(library, spec, templateProofOptions(library, id, spec, rename, options));
}

/** {@link proveAgainstTemplateIn} as a schedule of independent runs, for a
 * caller that executes them in parallel (each with
 * {@link runTemplateProofTaskIn}); its `result()` equals the sequential proof. */
export function templateProofScheduleIn(library: ComposeLibraryV1, id: string, spec: GameSpecV1, rename: Readonly<Record<string, string>>,
  options: TemplateProofOptionsV1 = {}): ProofScheduleV1 {
  return new ProofScheduleV1(spec, library.modules, templateProofOptions(library, id, spec, rename, options));
}

/** One task of a {@link templateProofScheduleIn} (same id, spec, rename and
 * options), in whichever thread runs it. */
export function runTemplateProofTaskIn(library: ComposeLibraryV1, id: string, spec: GameSpecV1, rename: Readonly<Record<string, string>>,
  options: TemplateProofOptionsV1, task: ProofTaskV1): ProofRunV1 {
  return runProofTask(spec, library.modules, templateProofOptions(library, id, spec, rename, options), task);
}

/** {@link runTemplateProofTaskIn}, yielding to the event loop (same run). */
export function runTemplateProofTaskAsyncIn(library: ComposeLibraryV1, id: string, spec: GameSpecV1, rename: Readonly<Record<string, string>>,
  options: TemplateProofOptionsV1, task: ProofTaskV1): Promise<ProofRunV1> {
  return runProofTaskAsync(spec, library.modules, templateProofOptions(library, id, spec, rename, options), task);
}

/** Every entry point bound to `library`. */
export function composeApi(library: ComposeLibraryV1) {
  return Object.freeze({
    library,
    validateSpec: (spec: unknown) => validateSpecIn(library, spec),
    compileSpec: (spec: unknown, options?: CompileOptionsV1) => compileSpecIn(library, spec, options),
    derivePresentation: (spec: unknown) => derivePresentationIn(library, spec),
    proveSpec: (spec: unknown, options?: Partial<ProofOptionsV1>) => proveSpecIn(library, spec, options),
    proveSpecAsync: (spec: unknown, options?: Partial<ProofOptionsV1>) => proveSpecAsyncIn(library, spec, options),
    listModules: () => listModulesIn(library),
    listTemplates: () => listTemplatesIn(library),
    getTemplate: (id: string) => getTemplateIn(library, id),
    instantiate: (id: string, overrides?: TemplateOverridesV1) => instantiateIn(library, id, overrides),
    patchTemplate: (id: string, patch: unknown) => patchTemplateIn(library, id, patch),
    specModuleVersions: (spec: GameSpecV1) => specModuleVersionsIn(library, spec),
    templateObligations: (id: string, spec: GameSpecV1, rename: Readonly<Record<string, string>>, structure?: Pick<TemplateStructureV1, 'signature' | 'events'>) =>
      templateObligationsIn(library, id, spec, rename, structure),
    proveAgainstTemplate: (id: string, spec: GameSpecV1, rename: Readonly<Record<string, string>>, options?: TemplateProofOptionsV1) =>
      proveAgainstTemplateIn(library, id, spec, rename, options),
    proveAgainstTemplateAsync: (id: string, spec: GameSpecV1, rename: Readonly<Record<string, string>>, options?: TemplateProofOptionsV1) =>
      proveAgainstTemplateAsyncIn(library, id, spec, rename, options),
    templateProofSchedule: (id: string, spec: GameSpecV1, rename: Readonly<Record<string, string>>, options?: TemplateProofOptionsV1) =>
      templateProofScheduleIn(library, id, spec, rename, options),
    runTemplateProofTask: (id: string, spec: GameSpecV1, rename: Readonly<Record<string, string>>, options: TemplateProofOptionsV1, task: ProofTaskV1) =>
      runTemplateProofTaskIn(library, id, spec, rename, options, task),
    runTemplateProofTaskAsync: (id: string, spec: GameSpecV1, rename: Readonly<Record<string, string>>, options: TemplateProofOptionsV1, task: ProofTaskV1) =>
      runTemplateProofTaskAsyncIn(library, id, spec, rename, options, task),
  });
}

export type ComposeApiV1 = ReturnType<typeof composeApi>;

// The maintained library's binding (each tree-shakes on its own).
export const validateSpec = (spec: unknown): SpecValidationV1 => validateSpecIn(MAINTAINED_LIBRARY, spec);
export const compileSpec = (spec: unknown, options: CompileOptionsV1 = {}): CompiledGameV1 => compileSpecIn(MAINTAINED_LIBRARY, spec, options);
export const derivePresentation = (spec: unknown): ComposedPresentationV1 => derivePresentationIn(MAINTAINED_LIBRARY, spec);
export const proveSpec = (spec: unknown, options: Partial<ProofOptionsV1> = {}): ProofResultV1 => proveSpecIn(MAINTAINED_LIBRARY, spec, options);
export const proveSpecAsync = (spec: unknown, options: Partial<ProofOptionsV1> = {}): Promise<ProofResultV1> =>
  proveSpecAsyncIn(MAINTAINED_LIBRARY, spec, options);
export const listModules = (): ModuleSummaryV1[] => listModulesIn(MAINTAINED_LIBRARY);
export const listTemplates = (): TemplateSummaryV1[] => listTemplatesIn(MAINTAINED_LIBRARY);
export const getTemplate = (id: string): TemplateV1 => getTemplateIn(MAINTAINED_LIBRARY, id);
export const instantiate = (id: string, overrides: TemplateOverridesV1 = {}): GameSpecV1 => instantiateIn(MAINTAINED_LIBRARY, id, overrides);
export const patchTemplate = (id: string, patch: unknown): TemplatePatchValidationV1 => patchTemplateIn(MAINTAINED_LIBRARY, id, patch);
export const specModuleVersions = (spec: GameSpecV1): Array<{ id: string; version: number }> => specModuleVersionsIn(MAINTAINED_LIBRARY, spec);
export const templateObligations = (id: string, spec: GameSpecV1, rename: Readonly<Record<string, string>>, structure?: Pick<TemplateStructureV1, 'signature' | 'events'>) =>
  templateObligationsIn(MAINTAINED_LIBRARY, id, spec, rename, structure);
export const proveAgainstTemplate = (id: string, spec: GameSpecV1, rename: Readonly<Record<string, string>>, options: TemplateProofOptionsV1 = {}): ProofResultV1 =>
  proveAgainstTemplateIn(MAINTAINED_LIBRARY, id, spec, rename, options);
export const proveAgainstTemplateAsync = (id: string, spec: GameSpecV1, rename: Readonly<Record<string, string>>,
  options: TemplateProofOptionsV1 = {}): Promise<ProofResultV1> => proveAgainstTemplateAsyncIn(MAINTAINED_LIBRARY, id, spec, rename, options);
export const templateProofSchedule = (id: string, spec: GameSpecV1, rename: Readonly<Record<string, string>>,
  options: TemplateProofOptionsV1 = {}): ProofScheduleV1 => templateProofScheduleIn(MAINTAINED_LIBRARY, id, spec, rename, options);
export const runTemplateProofTask = (id: string, spec: GameSpecV1, rename: Readonly<Record<string, string>>, options: TemplateProofOptionsV1,
  task: ProofTaskV1): ProofRunV1 => runTemplateProofTaskIn(MAINTAINED_LIBRARY, id, spec, rename, options, task);
export const runTemplateProofTaskAsync = (id: string, spec: GameSpecV1, rename: Readonly<Record<string, string>>, options: TemplateProofOptionsV1,
  task: ProofTaskV1): Promise<ProofRunV1> => runTemplateProofTaskAsyncIn(MAINTAINED_LIBRARY, id, spec, rename, options, task);
